import type { ReactNode } from 'react';
import { Building2, Layers, User } from 'lucide-react';

export type ScopeSelection =
  | { kind: 'personal' }
  | { kind: 'org' }
  | { kind: 'workspace'; workspaceId: string };

export type ScopeWorkspace = {
  _id: string;
  name: string;
  current: boolean;
};

function isSameScope(a: ScopeSelection, b: ScopeSelection) {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'workspace' && b.kind === 'workspace') return a.workspaceId === b.workspaceId;
  return true;
}

export function BrainScopeRail({
  title,
  subtitle,
  current,
  showPersonal,
  showOrg,
  showWorkspaces,
  workspaces,
  onSelect,
  footer,
}: {
  title: string;
  subtitle?: string;
  current: ScopeSelection;
  showPersonal?: boolean;
  showOrg?: boolean;
  showWorkspaces?: boolean;
  workspaces: ScopeWorkspace[];
  onSelect: (scope: ScopeSelection) => void;
  footer?: ReactNode;
}) {
  return (
    <aside className="brain-scope-rail" aria-label={`${title} scopes`}>
      <header className="brain-scope-rail-header">
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </header>

      {showPersonal && (
        <section className="brain-scope-section">
          <div className="brain-scope-section-label">Personal</div>
          <ScopeRow
            icon={<User size={14} strokeWidth={1.6} />}
            label="My knowledge"
            active={isSameScope(current, { kind: 'personal' })}
            onClick={() => onSelect({ kind: 'personal' })}
          />
        </section>
      )}

      {showOrg && (
        <section className="brain-scope-section">
          <div className="brain-scope-section-label">Organization</div>
          <ScopeRow
            icon={<Building2 size={14} strokeWidth={1.6} />}
            label="All of organization"
            active={isSameScope(current, { kind: 'org' })}
            onClick={() => onSelect({ kind: 'org' })}
          />
        </section>
      )}

      {(showWorkspaces || workspaces.length > 0) && (
        <section className="brain-scope-section">
          <div className="brain-scope-section-label">Workspaces</div>
          {workspaces.length === 0 ? (
            <ScopeRow
              icon={<Layers size={14} strokeWidth={1.6} />}
              label="No workspaces"
              active={false}
              disabled
              onClick={() => undefined}
            />
          ) : (
            workspaces.map((ws) => (
              <ScopeRow
                key={ws._id}
                icon={<Layers size={14} strokeWidth={1.6} />}
                label={ws.name}
                hint={ws.current ? 'Current' : undefined}
                active={isSameScope(current, { kind: 'workspace', workspaceId: ws._id })}
                onClick={() => onSelect({ kind: 'workspace', workspaceId: ws._id })}
              />
            ))
          )}
        </section>
      )}

      {footer && <div className="brain-scope-footer">{footer}</div>}
    </aside>
  );
}

function ScopeRow({
  icon,
  label,
  hint,
  active,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  hint?: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={active ? 'brain-scope-row active' : 'brain-scope-row'}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="brain-scope-row-icon">{icon}</span>
      <span className="brain-scope-row-label">{label}</span>
      {hint && <span className="brain-scope-row-hint">{hint}</span>}
    </button>
  );
}
